import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { BaseServicesService } from './services/base-services/base-services.service';
import { LoggingService } from './services/consoleLogs';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { DOCUMENT } from '@angular/common';
import { BrandingService } from './services/base-services/branding.service';
import { Branding, TenantUIResponse } from './models/tenant.model';
import { PermissionService } from './services/base-services/permission.service';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  // title = 'Careclinics | LMS';
  sessionUuid: string | null = null;
  logoWidth!: string;
  logoHeight!: string; loading: boolean = true;
  loginPath: string = ''; tenantUI!: TenantUIResponse;
  constructor(
    private baseServicesService: BaseServicesService,
    private permissionService: PermissionService,
    private loggingService: LoggingService,
    private meta: Meta, private title: Title,
    private titleService: Title,
    private brandingService: BrandingService,
    private cdr: ChangeDetectorRef,

    private metaService: Meta,
    private router: Router,
    @Inject(DOCUMENT) private doc: Document
  ) {


  }


  ngOnInit(): void {
    this.loadTenantUI();

    const token = localStorage.getItem('authToken');
    if (token) {
      this.permissionService.loadFromCache();
    }
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          this.permissionService.refreshPermissions(); // ✅ Always refresh
        }
      });

  }



  private loadTenantUI(): void {
    this.loggingService.log("private loadTenantUI app component")
    const cachedBranding = this.brandingService.getBrandingConfig();
    if (cachedBranding) {
      // Avoid blocking reload with heavy DOM/loop work.
      setTimeout(() => {
        this.applyBranding(cachedBranding);
        this.brandingService.setFavicon();
        this.brandingService.setMetaTags();
        this.loading = false;
      }, 0);
      return;
    }

    this.baseServicesService.getTenantUI().subscribe({
      next: (data: TenantUIResponse) => {
        this.tenantUI = data;

        localStorage.setItem('tenantUI', JSON.stringify(data));
        const branding = data?.data?.portalConfig?.branding;
        setTimeout(() => {
          if (branding) {
            this.applyBranding(branding);
          }
          this.brandingService.setFavicon();
          this.brandingService.setMetaTags();
          this.loading = false;  // hide spinner
        }, 0);
      },
      error: (err) => {
        this.loggingService.error('Error fetching tenant UI:', err);
        this.loading = false;
      }
    });
  }

  private applyBranding(branding: Branding): void {
    // Loop over branding keys and set CSS vars
    Object.keys(branding).forEach((key) => {
      if (typeof (branding as any)[key] === 'string') {
        const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        document.documentElement.style.setProperty(
          cssVarName,
          (branding as any)[key]
        );
      }
    });

    // ✅ Define a derived hover color for launch button
    if (branding.launchButton) {
      const hoverColor = this.shadeColor(branding.launchButton, -15); // darken ~15%
      document.documentElement.style.setProperty('--launch-button-hover', hoverColor);
    }

    // ✅ NEW: Create gradient variables for primary button
    if (branding.primaryButton) {
      this.createGradientVariables(branding.primaryButton);
    }

    // ✅ Keep logo logic
    if (branding.logo?.url) {
      this.loginPath = branding.logo.url;
    }
  }

  private createGradientVariables(baseColor: string): void {
    // Create lighter and darker variants for gradient
    const lighterVariant = this.shadeColor(baseColor, 20); // 20% lighter
    const darkerVariant = this.shadeColor(baseColor, -20); // 20% darker

    // Set gradient variables
    document.documentElement.style.setProperty('--primary-button-gradient-light', lighterVariant);
    document.documentElement.style.setProperty('--primary-button-gradient-dark', darkerVariant);

    // Set complete gradient CSS variables
    document.documentElement.style.setProperty(
      '--primary-button-gradient',
      `linear-gradient(135deg, ${lighterVariant}, ${baseColor}, ${darkerVariant})`
    );

    document.documentElement.style.setProperty(
      '--primary-button-gradient-horizontal',
      `linear-gradient(90deg, ${lighterVariant}, ${darkerVariant})`
    );

    document.documentElement.style.setProperty(
      '--primary-button-gradient-vertical',
      `linear-gradient(180deg, ${lighterVariant}, ${darkerVariant})`
    );
  }

  private shadeColor(color: string, percent: number): string {
    // Handle both hex and rgb colors
    let R, G, B;

    if (color.startsWith('#')) {
      // Hex color
      const hex = color.replace('#', '');
      R = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
      G = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
      B = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      // RGB color - simple extraction (for demo purposes)
      const rgbMatch = color.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        R = parseInt(rgbMatch[1]);
        G = parseInt(rgbMatch[2]);
        B = parseInt(rgbMatch[3]);
      } else {
        return color; // Return original if parsing fails
      }
    } else {
      return color; // Return original if format not recognized
    }

    // Calculate shaded values
    R = Math.min(255, Math.max(0, R + R * (percent / 100)));
    G = Math.min(255, Math.max(0, G + G * (percent / 100)));
    B = Math.min(255, Math.max(0, B + B * (percent / 100)));

    // Convert back to hex
    return `#${Math.round(R).toString(16).padStart(2, '0')}${Math.round(G).toString(16).padStart(2, '0')}${Math.round(B).toString(16).padStart(2, '0')}`;
  }


  callSessionApi(): void {
    this.baseServicesService.callSessionApi().subscribe({
      next: (response: any) => {
        const uuid = response?.uuid;
        this.baseServicesService.setSessionUuid(uuid);
        if (uuid) {
          this.loggingService.log(`Session API called successfully from app component: ${uuid}`);
        } else {
          this.loggingService.warn('UUID not found in the response.');
        }
      },
      error: (error) => {
        this.loggingService.error('Error calling session API', error);
      },
    });
  }

}
